import { useState } from 'react';

import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import LinkIcon from '@mui/icons-material/Link';
import { alpha, Box, Card, CardContent, Grid, Typography } from '@mui/material';
import AnimatedBackButton from 'components/elements/AnimatedBackButton';
import AnimatedCard from 'components/elements/AnimatedCard';
import HeaderComp from 'components/elements/HeadingComponent';
import { useNavigate } from 'react-router-dom';

import CreateMapping from './CreateMappings';
import DeleteMapping from './DeleteMapping';

const Mapping = () => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const navigate = useNavigate();

  const mappingOptions = [
    {
      id: 'create-mapping',
      title: 'Create mapping',
      description: 'Create new mappings between groups and permissions',
      icon: GroupAddIcon,
      onClick: () => setSelectedOption('Create mapping'),
      type: 'primary',
      color: '#6366f1', // Indigo
      bgColor: alpha('#6366f1', 0.08),
      borderColor: alpha('#6366f1', 0.2),
    },
    {
      id: 'delete-mapping',
      title: 'Delete mapping',
      description: 'Remove existing mappings and their associations',
      icon: GroupRemoveIcon,
      onClick: () => setSelectedOption('Delete mapping'),
      type: 'error',
      color: '#dc2626', // Red
      bgColor: alpha('#dc2626', 0.08),
      borderColor: alpha('#dc2626', 0.2),
    },
  ];

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      {selectedOption === 'Create mapping' ? (
        <>
          <CreateMapping onBackClick={() => setSelectedOption(null)} />
        </>
      ) : selectedOption === 'Delete mapping' ? (
        <>
          <DeleteMapping onBackClick={() => setSelectedOption(null)} />
        </>
      ) : (
        <>
          <HeaderComp>Mappings</HeaderComp>

          <AnimatedBackButton onClick={handleBackClick} />

          {/* Modern Mapping Options Grid */}
          <Box mb={4}>
            <Grid container spacing={3}>
              {mappingOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={option.id}>
                    <AnimatedCard
                      title={option.title}
                      description={option.description}
                      icon={<Icon />}
                      color={option.color}
                      bgColor={option.bgColor}
                      borderColor={option.borderColor}
                      isSelected={selectedOption === option.title}
                      onClick={option.onClick}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Quick Tips Section */}
          <Card
            sx={{
              background: '#fbfcfc',
              border: '1px solid',
              borderColor: 'rgba(0, 128, 128, 0.06)',
              borderRadius: 1.5,
              mb: 4,
              boxShadow: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'rgba(0, 128, 128, 0.15)',
                bgcolor: 'rgba(255, 255, 255, 0.6)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    p: 0.75,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <LinkIcon sx={{ fontSize: 18, color: 'white' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                    }}
                  >
                    Mapping Management
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      fontSize: '0.8rem',
                    }}
                  >
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <strong style={{ color: 'text.primary' }}>
                          Create mappings
                        </strong>{' '}
                        to establish relationships between groups and
                        permissions.
                      </Box>
                      <Box component="li">
                        <strong style={{ color: 'text.primary' }}>
                          Delete mappings
                        </strong>{' '}
                        when relationships are no longer needed or have changed.
                      </Box>
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default Mapping;
